import { formattedCurrency, withStyles, AccountBalanceResponse, AccountLimitsResponse } from 'vopay';
import { FunctionComponent, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavMenu } from '@/providers/NavMenu.provider';

import LottieFile from '../../components/LottieFile';
import LottieCollection from '../../LottieFiles/Collections.json';
import ExpandCard from '../../components/ExpandCard';

import type { LottieRefCurrentProps } from 'lottie-react';

import styles from './InteractiveInfo.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type TotalPaymentsCardProps = {
  accountBalanceResponse: AccountBalanceResponse;
  accountLimitsResponse: AccountLimitsResponse;
};

type FC = FunctionComponent<TotalPaymentsCardProps>;

const TotalCollectionsCard: FC = ({ accountBalanceResponse, accountLimitsResponse }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const collectionRef = useRef<LottieRefCurrentProps>(null);

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  const parseAmount = (value: string | undefined | null): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  return (
    <ExpandCard
      align="end"
      buttonText={t('Widgets.InteractiveInfo.openSingleCollection')}
      onMouseOver={() => handleLottieFileHover(collectionRef, true)}
      onMouseOut={() => handleLottieFileHover(collectionRef, false)}
      onNavigateClick={() => onSubNavClick('singleCollection', '/single-collection', t('collection'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalMonthlyCollections')}
            </Title>
            <div className={cn('totalCardCurrency')}>
              <Title level="5" emphasized={false}>
                {accountBalanceResponse?.Currency}
              </Title>
              <Title level="3" className={cn('totalCardAmount')}>
                {formattedCurrency(
                  String(
                    parseAmount(accountLimitsResponse?.Inbound?.Limits?.Monthly ?? 0) -
                      parseAmount(accountLimitsResponse?.Inbound?.RemainingLimits?.Monthly ?? 0),
                  ),
                )}
              </Title>
            </div>
          </div>
          <LottieFile animationData={LottieCollection} lottieRef={collectionRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.dailyCollections')}</div>
            <div>
              {accountBalanceResponse.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Inbound?.Limits?.Daily ?? 0) -
                    parseAmount(accountLimitsResponse?.Inbound?.RemainingLimits?.Daily ?? 0),
                ),
              )}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.weeklyCollections')}</div>
            <div>
              {accountBalanceResponse.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Inbound?.Limits?.Weekly ?? 0) -
                    parseAmount(accountLimitsResponse?.Inbound?.RemainingLimits?.Weekly ?? 0),
                ),
              )}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.monthlyCollections')}</div>
            <div>
              {accountBalanceResponse.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Inbound?.Limits?.Monthly ?? 0) -
                    parseAmount(accountLimitsResponse?.Inbound?.RemainingLimits?.Monthly ?? 0),
                ),
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default TotalCollectionsCard;
